// =====================================================================
// auth-session.ts — Cached member + safe Supabase session handling
// =====================================================================
// Avoids calling auth.getUser() on every db.* mutation (network hang
// after idle tabs). Uses getSession() for the hot path and getUser()
// only on explicit refresh (app bootstrap).

import type { AuthChangeEvent } from '@supabase/supabase-js';
import { memberDisplayName } from '@/lib/display-name';
import { getSupabase } from './client';
import type { Member } from './types';

const AUTH_TIMEOUT_MS = 15_000;

let cachedMember: Member | null = null;
let cachedAuthUserId: string | null = null;
let visibilityHookInstalled = false;

export class AuthSessionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthSessionError';
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new AuthSessionError(`${label} timed out — try again`));
    }, ms);
    promise
      .then((v) => { clearTimeout(timer); resolve(v); })
      .catch((e) => { clearTimeout(timer); reject(e); });
  });
}

async function fetchMemberByAuthUserId(authUserId: string): Promise<Member | null> {
  const { data, error } = await getSupabase()
    .from('members')
    .select('*')
    .eq('auth_user_id', authUserId)
    .is('deleted_at', null)
    .single();
  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  const member = data as Member;
  return { ...member, name: memberDisplayName(member) };
}

function setCache(member: Member | null, authUserId: string | null = member?.auth_user_id ?? null): void {
  cachedMember = member;
  cachedAuthUserId = authUserId;
}

export function clearMemberCache(): void {
  cachedMember = null;
  cachedAuthUserId = null;
}

export function patchMemberCache(patch: Partial<Member>): void {
  if (cachedMember) {
    cachedMember = { ...cachedMember, ...patch };
  }
}

async function getLocalAuthUserId(): Promise<string | null> {
  const { data: { session } } = await getSupabase().auth.getSession();
  return session?.user?.id ?? null;
}

/** Hot path: getSession + optional members query. No getUser() network round-trip. */
export async function getCachedMember(): Promise<Member | null> {
  const authUserId = await getLocalAuthUserId();
  if (!authUserId) {
    clearMemberCache();
    return null;
  }

  if (cachedMember && cachedAuthUserId === authUserId) {
    return cachedMember;
  }

  const member = await fetchMemberByAuthUserId(authUserId);
  setCache(member, authUserId);
  return member;
}

/** Bootstrap / retry: validate JWT with Supabase then reload member. */
export async function refreshMember(): Promise<Member | null> {
  const { data: { user }, error } = await withTimeout(
    getSupabase().auth.getUser(),
    AUTH_TIMEOUT_MS,
    'Session check',
  );
  if (error) throw error;
  if (!user) {
    clearMemberCache();
    return null;
  }

  const member = await fetchMemberByAuthUserId(user.id);
  setCache(member, user.id);
  return member;
}

function shouldNotifyMemberChange(event: AuthChangeEvent): boolean {
  return event === 'SIGNED_IN'
    || event === 'SIGNED_OUT'
    || event === 'INITIAL_SESSION'
    || event === 'USER_UPDATED'
    || event === 'TOKEN_REFRESHED';
}

function logAuthDebug(label: string, detail?: Record<string, unknown>): void {
  if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
    console.debug(`[auth] ${label}`, detail ?? '');
  }
}

export async function logAuthBootstrap(): Promise<void> {
  const { data: { session } } = await getSupabase().auth.getSession();
  logAuthDebug('bootstrap', {
    hasSession: Boolean(session),
    expiresAt: session?.expires_at ?? null,
    userId: session?.user?.id ?? null,
  });
  if (session) {
    const member = await getCachedMember();
    logAuthDebug('member cache', { found: Boolean(member), memberId: member?.id ?? null });
  }
}

export function subscribeAuthChanges(cb: (member: Member | null) => void): () => void {
  const { data: { subscription } } = getSupabase().auth.onAuthStateChange((event) => {
    logAuthDebug('state change', { event });
    // Never call getUser() inside this callback — defer and use getSession path.
    setTimeout(() => {
      void (async () => {
        try {
          if (event === 'SIGNED_OUT') {
            clearMemberCache();
            cb(null);
            return;
          }

          if (shouldNotifyMemberChange(event)) {
            if (event === 'TOKEN_REFRESHED') {
              clearMemberCache();
            }
            const member = await getCachedMember();
            logAuthDebug('member after change', { event, found: Boolean(member) });
            cb(member);
          }
        } catch (err) {
          logAuthDebug('change handler error', { event, message: (err as Error).message });
        }
      })();
    }, 0);
  });

  return () => subscription.unsubscribe();
}

export function installVisibilityRefresh(): void {
  if (visibilityHookInstalled || typeof document === 'undefined') return;
  visibilityHookInstalled = true;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;

    const sb = getSupabase();
    sb.auth.startAutoRefresh();

    void (async () => {
      const { data: { session } } = await sb.auth.getSession();
      if (!session) return;

      const expiresAt = session.expires_at ?? 0;
      const now = Math.floor(Date.now() / 1000);
      if (expiresAt - now >= 60) return;

      try {
        await withTimeout(sb.auth.refreshSession(), AUTH_TIMEOUT_MS, 'Session refresh');
      } catch {
        // User actions can call auth.refresh() if needed.
      }
    })();
  });
}
